/**
 * @voltxampere/ui — Canvas theme palette (Sprint 12)
 *
 * `ZENIT_THEME` is the JS-side mirror of `apps/web/src/styles/tokens.css`.
 * The schematic canvas runs Canvas2D draw calls on the hot path
 * (500 components × dozens of strokes each per frame); calling
 * `getComputedStyle` per stroke would burn the 16 ms frame budget
 * for negligible gain. We pay the cost once at module load and then
 * the renderer just reads string literals.
 *
 * `tokens.css` stays canonical. `theme-sync.test.ts` parses
 * `tokens.css` on every test run and asserts every value below
 * matches. Any divergence (e.g. brand-bible v1.0.2 amber tweak)
 * must update **both** files in the same commit — the guard test
 * fails otherwise.
 *
 * See ADR-0016.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Mirrors `apps/web/src/styles/tokens.css`. Brand-bible v1.0.1
 * disambiguated `--ch-3` (amber `#cc8844`) from `--brass`; the
 * guard test asserts the two stay distinct so a future brand
 * revision cannot silently re-collide them.
 *
 * Sprint 18 D0 — palette migrated to zenit-platform 2 / zenit-
 * foundation 2 mockup canonical values (lower saturation + blue-
 * night tone). See ADR-0034 + REFERENCE_PARITY_AUDIT.md §0.1.
 */
export const ZENIT_THEME = {
    bg: {
        void: "#05080f",
        ink: "#0b1020",
        ink2: "#10172b",
        ink3: "#192137",
        ink4: "#232b47",
    },
    text: {
        ivory: "#efe6d2",
        dim: "rgba(239, 230, 210, 0.6)",
        faint: "rgba(239, 230, 210, 0.28)",
        wisp: "rgba(239, 230, 210, 0.08)",
        ghost: "rgba(239, 230, 210, 0.04)",
    },
    accent: {
        brass: "#c08a3e",
        brassHot: "#e8b35c",
        brassDim: "rgba(192, 138, 62, 0.45)",
        brassFaint: "rgba(192, 138, 62, 0.18)",
        brassGhost: "rgba(192, 138, 62, 0.08)",
    },
    status: {
        success: "#7a9f68",
        danger: "#b04a3e",
        starWhite: "#fffbeb",
    },
    channel: {
        ch1: "#7bb896",
        ch2: "#6ea3c8",
        ch3: "#cc8844",
        ch4: "#a78bc8",
    },
    // Stroke widths inherit from Sprint 10.1 symbol library
    // (constants.ts STROKE_WIDTH_MAIN/HEAVY/DETAIL). `selected` is
    // the only canvas-specific addition.
    stroke: {
        default: 1.4,
        thick: 1.6,
        selected: 2,
    },
};
//# sourceMappingURL=theme.js.map